import React, { useState } from "react";
import { Sentence } from "../../kit";

// Per-tint card surface: a muted background + matching border so each insight
// type (warm / cool / positive) reads as a distinct, gentle signal. Falls back
// to `cool` when the tint is missing or unrecognised.
const TINT_CARD = {
  warm: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800",
  cool: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800",
  positive: "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800",
};

// Maps the `takeaway.panel` category key to a short display label. Unknown
// keys produce no label (the card renders without one rather than guessing).
const PANEL_LABELS = {
  queue: "Queue",
  aging: "Aging",
  waiting: "Waiting",
  blocked: "Blocked",
  stalled: "Stalled",
};

// ─── Shared: TakeawayCard ───────────────────────────────────────────────────────
// Insight surface card. Renders `takeaway.says` verbatim via <Sentence> with the
// tint colour from the local TINT_CARD map (falls back to `cool`). The tint
// background + border provide enough visual identity on their own; a small
// panel label sits above the sentence.
// The card itself is no longer fully clickable. The says text toggles
// `textExpanded` on click (line-clamp removed when expanded), with a
// "Show more" / "Show less" hint for long sentences. A "View" button at the
// bottom calls `onNavigate(takeaway.drill)` for direct navigation — no
// DrillSheet. When `takeaway.drill` is absent the "View" button is omitted.
const TakeawayCard = ({ takeaway, onClientClick, onNavigate, onDismiss }) => {
  const [textExpanded, setTextExpanded] = useState(false);
  const drill = takeaway?.drill;
  const canNavigate = !!(drill && onNavigate);
  // Destination hint — shows the user where the View button will take them,
  // derived from the drill type (no extra fetch; the takeaways RPC carries no
  // client name, so client_waiting falls back to a generic "Client").
  const navigateLabel = (() => {
    if (!drill?.what) return null;
    const bucketLabels = { unowned: "Unowned", waiting_on_us: "Waiting on Us", waiting_on_them: "Waiting on Them", waiting_on_me: "Mine" };
    switch (drill.what) {
      case "bucket": return bucketLabels[drill.id] || "Bucket";
      case "past_p90": return "Unowned";
      case "client_waiting": return "Waiting on Them";
      case "blocked":
      case "stalled": return "Waiting on Us";
      default: return null;
    }
  })();
  const className = `relative text-left w-full ${textExpanded ? "min-h-[140px]" : "h-[140px] overflow-hidden"} flex flex-col rounded-lg border p-2.5 cursor-default ${TINT_CARD[takeaway.tint] || TINT_CARD.cool}`;
  const panelLabel = PANEL_LABELS[takeaway.panel];
  const longSays = (takeaway.says?.length || 0) > 80;
  const inner = (
    <>
      {panelLabel && (
        <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 dark:text-gray-500 mb-1">{panelLabel}</span>
      )}
      <div className="flex items-start gap-1.5 pr-6">
      {/* Dismiss button — session-only cleanup, top-right. stopPropagation keeps the toggle / navigation clicks from firing. */}
      {onDismiss && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDismiss(takeaway.panel); }}
          aria-label="Dismiss takeaway"
          className="absolute top-2 right-2 p-0.5 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200 opacity-60 hover:opacity-100 transition-opacity"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
      {/* Briefcase icon — work-related, hardcoded (ignores any RPC icon value) */}
      <svg
        className="w-3.5 h-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
      <Sentence
        says={takeaway.says}
        tint={takeaway.tint}
        size="none"
        title={takeaway.says}
        onClick={(e) => { e.stopPropagation(); setTextExpanded(!textExpanded); }}
        className={`cursor-pointer text-sm text-gray-800 dark:text-gray-100 ${textExpanded ? "" : "line-clamp-3"}`}
      />
    </div>
      {longSays && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setTextExpanded(!textExpanded); }}
          className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 mt-0.5"
        >
          {textExpanded ? "Show less" : "Show more"}
        </button>
      )}
      {canNavigate && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNavigate(takeaway.drill); }}
          aria-label={`View — ${navigateLabel || "more"}`}
          className="absolute bottom-1.5 right-2 flex items-center gap-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
        >
          {navigateLabel && (
            <span className="text-[10px] leading-none">{navigateLabel}</span>
          )}
          <svg
            className="w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      )}
    </>
  );
  return <div className={className}>{inner}</div>;
};

export default TakeawayCard;
